import React, { useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { Button, Space } from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import ListeRedditionPDF from "../Pdf/ListeRedditionPDF";
import RelevePDF from "../Pdf/RelevePDF";
import HonorairePdf from "../Pdf/HonorairePdf";
import PdfReaderModal from "../modals/PdfReaderModal";
import { HttpMethod, initFetch, fetchWithRetry } from "../../utils/fetcher";

const getDestinataire = (utilisateur) => {
  const societe = utilisateur.utilisateur_societe;

  return `${societe.societe_zone_libre_1 ? societe.societe_zone_libre_1 + "<br/>" : ""}${
    societe.societe_zone_libre_2 ? societe.societe_zone_libre_2 + "<br/>" : ""
  }${societe.societe_zone_libre_3 ? societe.societe_zone_libre_3 + "<br/>" : ""}${
    societe.societe_zone_libre_4 ? societe.societe_zone_libre_4 + "<br/>" : ""
  }${societe.societe_zone_libre_5 ? societe.societe_zone_libre_5 + "<br/>" : ""}`;
};

const createPdfDocument = (pdf) => {
  const { url, params } = initFetch("pdfs", HttpMethod.POST);

  return fetchWithRetry(url, { ...params, body: JSON.stringify(pdf) }).then((response) =>
    response.json()
  );
};

const getFileFromUrl = (id, type = "b") => {
  const { url, params } = initFetch(
    `api/v1/pdfs/${id}/render/${type}`,
    HttpMethod.GET,
    {
      Authorization: true,
      formData: false,
      pdf: true,
    }
  );

  return fetchWithRetry(url, params).then((response) => response.blob());
};

const renderReleve = ({ utilisateur, dateButoir, detaillee, tva, ligneReddition }) =>
  ReactDOMServer.renderToStaticMarkup(
    <RelevePDF
      utilisateur={utilisateur}
      dateButoir={dateButoir}
      detaillee={detaillee}
      tva={tva}
      ligneReddition={ligneReddition}
    />
  );

const renderHonoraire = ({ utilisateur, dateButoir, ligneReddition, numeroFacture }) =>
  ReactDOMServer.renderToStaticMarkup(
    <HonorairePdf
      utilisateur={utilisateur}
      dateButoir={dateButoir}
      ligneReddition={ligneReddition}
      numeroFacture={numeroFacture}
    />
  );

export const prepareRedditionPdf = ({
  utilisateur,
  dateButoir,
  detaillee,
  tva,
  ligne,
  affectation,
  tags,
  saveDocument,
  onSaved,
  onError,
}) => {
  const html = renderReleve({
    utilisateur,
    dateButoir,
    detaillee,
    tva,
    ligneReddition: ligne,
  });
  const pdf = {
    titre: "Releve",
    destinataire: getDestinataire(utilisateur),
    pdf_page: [{ html }],
  };

  createPdfDocument(pdf)
    .then((result) =>
      getFileFromUrl(result.pdf.id).then((blob) => {
        const file = blob;
        file.lastModifiedDate = new Date();
        file.name = `Releve_de_Gestion_du_${dayjs().format("DD-MM-YYYY")}_Num-${
          result.pdf.id
        }.pdf`;

        if (tags.length > 0) {
          return saveDocument(
            { file },
            affectation.proprietaire_affectation_dossier,
            tags[0]
          ).then(onSaved);
        } else {
          toast.error(
            "Aucun tag de type Propriétaire configuré : le relevé de gestion n'a pas été enregistré !",
            { containerId: "A" }
          );
        }
      })
    )
    .catch((err) => onError(err));
};

export const prepareFactureHonoraires = ({
  utilisateur,
  dateButoir,
  ligne,
  affectation,
  tags,
  saveDocument,
  compteurFactureHonoraires,
  onSaved,
  onError,
}) => {
  if (!utilisateur.utilisateur_societe.invoice_status) {
    onSaved();
    return;
  }

  if (parseFloat(ligne.montantFrais) === 0) {
    onSaved();
    return;
  }

  compteurFactureHonoraires.current += 1;

  const html = renderHonoraire({
    utilisateur,
    dateButoir,
    ligneReddition: ligne,
    numeroFacture: compteurFactureHonoraires.current,
  });
  const pdf = {
    titre: "Facture honoraires",
    destinataire: getDestinataire(utilisateur),
    pdf_page: [{ html }],
  };

  createPdfDocument(pdf)
    .then((result) =>
      getFileFromUrl(result.pdf.id).then((blob) => {
        const file = blob;
        file.lastModifiedDate = new Date();
        file.name = `Facture_Honoraires_du_${dayjs().format("DD-MM-YYYY")}_Num-${
          result.pdf.id
        }.pdf`;

        if (tags.length > 0) {
          return saveDocument(
            { file },
            affectation.proprietaire_affectation_dossier,
            tags[0]
          ).then(onSaved);
        } else {
          toast.error(
            "Aucun tag de type Propriétaire configuré : la facture d'honoraires n'a pas été enregistrée !",
            { containerId: "A" }
          );
        }
      })
    )
    .catch((err) => onError(err));
};

const Pdf = ({ utilisateur, dateButoir, detaillee, tva, lignesReddition, children }) => {
  const [modalPdfReader, setModalPdfReader] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfReaderTitle, setPdfReaderTitle] = useState(null);

  useEffect(
    () => () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    },
    [pdfUrl]
  );

  const openPdfReader = (id, type, title) => {
    getFileFromUrl(id, type).then((blob) => {
      setPdfUrl(URL.createObjectURL(blob));
      setPdfReaderTitle(title);
      setModalPdfReader(true);
    });
  };

  const createPdfListe = () => {
    const html = ReactDOMServer.renderToStaticMarkup(
      <ListeRedditionPDF
        utilisateur={utilisateur}
        dateButoir={dateButoir}
        lignesReddition={lignesReddition}
      />
    );
    const pdf = {
      titre: "Liste préparatoire",
      destinataire: "",
      pdf_page: [{ html }],
    };

    createPdfDocument(pdf).then((result) => {
      openPdfReader(result.pdf.id, "a", "Liste préparatoire");
    });
  };

  const createPdf = () => {
    const pdf = {
      titre: "Releve",
      destinataire: getDestinataire(utilisateur),
      pdf_page: lignesReddition.map((ligne) => ({
        html: renderReleve({
          utilisateur,
          dateButoir,
          detaillee,
          tva,
          ligneReddition: ligne,
        }),
      })),
    };

    createPdfDocument(pdf).then((result) => {
      openPdfReader(result.pdf.id, "b", "Relevé de Gérance");
    });
  };

  const createFactureHonoraires = () => {
    const lignesAvecHonoraires = lignesReddition.filter(
      (ligne) => parseFloat(ligne.montantFrais) > 0
    );

    if (lignesAvecHonoraires.length === 0) {
      toast.info("Aucun honoraire à facturer sur la sélection.", { containerId: "A" });
      return;
    }

    const pdf = {
      titre: "Facture honoraires",
      destinataire: getDestinataire(utilisateur),
      pdf_page: lignesAvecHonoraires.map((ligne, index) => ({
        html: renderHonoraire({
          utilisateur,
          dateButoir,
          ligneReddition: ligne,
          numeroFacture: index + 1,
        }),
      })),
    };

    createPdfDocument(pdf).then((result) => {
      openPdfReader(result.pdf.id, "b", "Facture honoraires");
    });
  };

  const closePdfReader = () => {
    setModalPdfReader(false);
    setPdfUrl(null);
    setPdfReaderTitle(null);
  };

  const invoiceEnabled = utilisateur.utilisateur_societe.invoice_status;

  return (
    <>
      <PdfReaderModal
        open={modalPdfReader}
        onCancel={closePdfReader}
        pdfUrl={pdfUrl}
        title={pdfReaderTitle}
      />

      <Space wrap>
        <Button onClick={createPdfListe}>Récap. sélection prop.</Button>
        {children}
        {invoiceEnabled && (
          <Button
            onClick={createFactureHonoraires}
            disabled={!lignesReddition.some((ligne) => parseFloat(ligne.montantFrais) > 0)}
          >
            Facture honoraires
          </Button>
        )}
        <Button type="primary" onClick={createPdf}>
          Générer PDF
        </Button>
      </Space>
    </>
  );
};

export default Pdf;
